import {
  ClientConnectItem,
  ClientScreen,
  ClientStudent,
  ConnectAnswerRequest,
  ConnectFinalStatePair,
  ConnectFinishedItem,
  connectItemType,
  DEFAULT_DRAW_IMAGE,
  DragDropFinalState,
  emptyConnect,
  emptyDragDrop,
  emptyDrawing,
  emptyKeyboard,
  emptyMap,
  emptyOpenText,
  emptyTimerState,
  FinishScreenOptions,
  invalidMapPin,
  JoinRequestError,
  JoinResponse,
  NextTimerState,
  PlayConnectItem,
  PlayConnectPair,
  PlayDragDropAnswer,
  PlayDragDropState,
  PlayErrorAction,
  PlayHubState,
  ReconnectResponse,
  ReconnectTeacherResponse,
  ReviewData,
  ReviewScreenResponse,
  ScreenFinishedResponse,
  SessionMode,
  setGlobalDontPreventBrowserClose,
  StartedResponse,
} from "play/play-model"
import playEnvironment from "play/play-environment"
import { startListeningForMessages } from "play/pending-messages"
import {
  atOrNull,
  buildLoginUrl,
  buildLoginUrlWithGameCode,
  getHtmlLanguage,
  hasText,
  yesno,
} from "utils/helpers"
import {
  serverPlayConnectAnswer,
  serverPlayDragDropAnswer,
  serverPlayDrawingSubmit,
  serverPlayKeyboardAnswer,
  serverPlayMapAnswer,
  serverPlayNextScreen,
  serverPlayOpenTextAnswer,
  serverPlayPollAnswer,
  serverPlayQuizAnswer,
  serverPlayReviewNext,
} from "utils/server"
import {
  globalModifyPlayState,
  oncePerUpdate,
} from "play/context/play-context"
import { generateLogoutLink } from "play/hooks/logout-hook"
import { sanitizeEditorPreviewHtml } from "utils/html-sanitization"
import {
  ConnectPair,
  DragDropAnswer,
  DragDropCategory,
  ScreenType,
} from "models/tables"
import { ServerEvent, ServerRequestErrorEvent } from "utils/server-api"
import { createPlayError } from "play/error-report"

export function studentJoinedReducer(state: PlayHubState, resp: JoinResponse) {
  console.log("studentJoinRequestFinished")

  state.serverError = null
  state.countScreens = resp.countScreens
  state.timer.visible = resp.timerVisible
  state.reportId = resp.reportId || null
  state.theme = resp.theme || null

  if (resp.error === JoinRequestError.none) {
    state.connectionId = resp.connectionId
    state.mode = resp.sessionMode
    state.className = resp.className
    state.disableMemes = resp.disableMemes
    // note: start as not in review
    state.isReview = false
    state.canReviewNextScreen = resp.canReviewNextScreen
    state.canReviewPrevScreen = resp.canReviewPrevScreen
    state.isStudentRemoved = false
    state.isBusinessVersion = resp.isBusinessVersion
    state.newGameLink = resp.newGameLink
    state.newGameMessage = resp.newGameText

    playEnvironment.connectionId = state.connectionId

    if (resp.sessionMode === SessionMode.Classroom) {
      startListeningForMessages({
        firstTime: true,
        hasTimer: resp.timerEnabledForSession,
      })
    }

    if (!resp.screen || resp.waitForOtherStudents) {
      state.loadingReason = "ClassroomLobby"
      state.internalPage = "Loading"
      state.nextTimer = resp
    } else {
      gameStartedReducer(state, {
        screen: resp.screen,
        reportId: resp.reportId || null,
        imagesToPreload: resp.uniqueUrls || [],
        connectionId: resp.connectionId,
        countStartedStudents: state.students.length,
        countScreens: resp.countScreens,
        timerRunning: resp.timerRunning,
        timerSecondsRemaining: resp.timerSecondsRemaining,
        timerVisible: resp.timerVisible,
        timerTotalSeconds: resp.timerTotalSeconds,
        timerEnabledForSession: resp.timerEnabledForSession,
      })
    }
  } else {
    joinRequestErrorReducer(state, { error: resp.error })
  }
}

export function teacherReconnectSuccessfulReducer(
  state: PlayHubState,
  resp: ReconnectTeacherResponse
) {
  state.screen = resp.screen
  state.screenIndex = resp.currentScreenIndex
  state.students = resp.students
  state.studentsInLobby = resp.lobby
  state.connectionId = resp.newConnectionId
  state.teacherConnectionId = resp.newConnectionId
  state.gameCode = resp.gameCode
  state.joinError = null
  state.isTeacherUrl = true
  state.disableMemes = resp.memesAreDisabled
  state.countScreens = resp.countScreens
  state.timer.visible = resp.timerVisible
  state.timer.enabledForSession = resp.timerEnabledForSession
  state.lessonName = resp.lessonName
  state.lessonImage = resp.lessonImage || null
  state.classId = state.classId || null
  state.className = resp.className || null
  state.enableGraphicalLogin = !!resp.enableGraphicalLogin

  playEnvironment.connectionId = resp.newConnectionId
  playEnvironment.oldGameCode = resp.gameCode

  if (screen) {
    applyScreenToState(state, resp.screen)

    if (state.imagesToPreload.length > 0) {
      state.loadingReason = "LoadingImages"
      state.internalPage = "Loading"
      //state.nextTimer = action.payload || null;
    } else {
      state.internalPage = null
    }
  } else {
    state.internalPage = "Lobby"
  }

  startListeningForMessages({
    firstTime: true,
    hasTimer: resp.timerEnabledForSession,
  })
}

export function gameStartedReducer(
  state: PlayHubState,
  payload: StartedResponse
) {
  const {
    screen,
    imagesToPreload,
    connectionId,
    countStartedStudents,
    countScreens,
    reportId,
  } = payload

  state.screen = screen
  state.imagesToPreload = [...imagesToPreload, DEFAULT_DRAW_IMAGE]
  state.teacherConnectionId = connectionId
  state.countScreens = countScreens
  state.reportId = reportId

  applyScreenToState(state, screen)

  state.internalPage = "Loading"
  state.loadingReason = "LoadingImages"
  state.nextTimer = payload

  if (countStartedStudents > 0) {
    state.countStartedStudents = countStartedStudents
  }

  playEnvironment.connectionId = state.connectionId
  playEnvironment.oldGameCode = state.gameCode
}

export function joinRequestErrorReducer(
  state: PlayHubState,
  payload: { error: JoinRequestError }
) {
  state.joinError = payload.error

  console.log(
    "joinRequestError",
    payload.error,
    JoinRequestError[payload.error]
  )

  switch (payload.error) {
    case JoinRequestError.requireGraphicalLogin: {
      let redirecturl: string
      if (hasText(state.gameCode)) {
        redirecturl = buildLoginUrlWithGameCode(state.gameCode)
      } else {
        console.warn("Cannot do graphical login when game code is empty")
        redirecturl = buildLoginUrl()
      }
      state.loginUrl = redirecturl
      break
    }

    case JoinRequestError.requireLogin: {
      let redirecturl: string
      if (hasText(state.gameCode)) {
        redirecturl = buildLoginUrlWithGameCode(state.gameCode)
      } else {
        redirecturl = buildLoginUrl()
      }
      state.loginUrl = redirecturl
      break
    }

    case JoinRequestError.requireUsername: {
      if (state.internalPage === "UserName") {
        state.joinError = JoinRequestError.requireUsername
      } else {
        state.joinError = null
        state.internalPage = "UserName"
      }
      break
    }

    case JoinRequestError.alreadyPlayed:
    case JoinRequestError.gameCodeExpired:
    case JoinRequestError.gameCodeInactive:
    case JoinRequestError.lessonHasNoScreens:
    case JoinRequestError.gameCodeMissing: {
      state.internalPage = "GameCode"
      break
    }

    case JoinRequestError.usernameTaken: {
      if (!state.loggedUser) {
        state.internalPage = "UserName"
      }
      break
    }
  }
}

export function studentRemovedReducer(
  state: PlayHubState,
  payload: ClientStudent
) {
  const student = payload

  if (!student || student.connectionId === state.connectionId) {
    console.log("stop listening for messages")
    state.isStudentRemoved = true
  }

  console.log("studentRemovedAction")
  if (state.connectionId === state.teacherConnectionId) {
    let connectionIdToRemove: string

    if (student) {
      connectionIdToRemove = student.connectionId
    } else if (!state.isTeacherUrl && state.connectionId) {
      connectionIdToRemove = state.connectionId
    }

    const idx = state.students.findIndex(
      (x) => x.connectionId === connectionIdToRemove
    )
    if (idx !== -1) {
      if (hasText(state.classId)) {
        // when started with class, grey out item
        state.students[idx].hasJoined = false
      } else {
        // when started anonymously, remove from lobby
        state.students.splice(idx, 1)
      }
    }

    let idxLobby: number
    if (!!student) {
      idxLobby = state.studentsInLobby.findIndex(
        (x) => x.connectionId === student.connectionId
      )
    } else {
      idxLobby = state.studentsInLobby.findIndex(
        (x) => x.connectionId === connectionIdToRemove
      )
    }

    if (idxLobby === -1) {
      // do nothing, already removed
    } else {
      if (hasText(state.classId)) {
        // when started with class, grey out item
        state.studentsInLobby[idxLobby].hasJoined = false
      } else {
        // when started anonymously, remove from lobby
        state.studentsInLobby.splice(idxLobby, 1)
      }
    }
  } else if (student.connectionId === state.connectionId) {
    setGlobalDontPreventBrowserClose(true)
    connectionClosedReducer(state, { logout: true })
  } else {
    console.log(
      "Trying to remove student %s, but neither self (%s), nor teacher",
      student.connectionId,
      state.connectionId
    )
  }
}

function isIgnoredMessage(event: ServerEvent | undefined): boolean {
  if (typeof event == "undefined") {
    return false
  }
  const lang = getHtmlLanguage()
  const re = new RegExp(`^\/${lang}\/api\/playhub\/pending-messages\/`, "gi")
  return re.test(event.url)
}

export function genericServerRequestErrorReducer(
  state: PlayHubState,
  payload?: ServerRequestErrorEvent | undefined
) {
  if (isIgnoredMessage(payload)) return

  state.isServerRequestInProgress = false
  state.nextScreenButtonIsActive = true
  if (payload) {
    console.log(
      "HTTP(error) %s %s\n%s",
      payload.method,
      payload.url,
      JSON.stringify(payload.error)
    )
  }
}

export function connectionClosedReducer(
  state: PlayHubState,
  opts?: { logout?: boolean }
) {
  console.log("connectionClosedReducer", opts)
  state.connectionId = null
  state.fullName = null
  state.joinError = null

  playEnvironment.connectionId = null
  playEnvironment.userName = null
  playEnvironment.fullName = null

  if (state.internalPage !== "FinalScreen") {
    console.log("logging out")

    const locale_code = getHtmlLanguage()
    let newLocation = generateLogoutLink({
      isTeacher: state.isTeacherUrl,
      locale_code,
      lessonId: state.lessonId || "",
      classId: state.classId || "",
      timerEnabled: state.timer.enabledForSession,
      disableMemes: state.disableMemes,
      enableGraphicalLogin: state.enableGraphicalLogin,
    })

    if (opts?.logout === true) {
      newLocation = `/${locale_code}/account/logout?returnUrl=${encodeURIComponent(newLocation)}`
    }

    location.replace(newLocation)
  }
}

export function pendingMessagesFailedReducer(
  state: PlayHubState,
  reason?: unknown
) {
  state.isReconnecting = false
  state.playError = createPlayError("Messages", reason, "fatal")
}

/**
 * Tells the player that something they did has failed (PlayErrorNotice shows
 * it and reports it to the server). A lost connection stays on screen: it
 * is the bigger problem and already asks for a reload.
 */
export function playErrorReducer(
  state: PlayHubState,
  action: PlayErrorAction,
  e: unknown
) {
  if (state.playError?.level === "fatal") return
  state.playError = createPlayError(action, e)
}

/** Takes the message away when the player dismisses it or tries again */
export function clearPlayErrorReducer(state: PlayHubState) {
  if (state.playError && state.playError.level !== "fatal") {
    state.playError = null
  }
}

export function reconnectSuccessfulReducer(
  state: PlayHubState,
  payload: ReconnectResponse
) {
  const {
    screen,
    points,
    newSocketId,
    gameCode,
    mode,
    disableMemes,
    fullName,
    avatarId,
    isTeacher,
    connectItems,
    isReview,
    canReviewNextScreen,
    canReviewPrevScreen,
    countScreens,
    className,
    originalScreen,
    reportedAnswer,
    gameStarted,
    classId,
    isBusinessVersion,
    theme,
  } = payload

  if (state.loggedUser) {
    state.loggedUser.name = fullName
    state.loggedUser.avatarId = avatarId
  }

  state.screen = screen
  state.points = points
  state.fullName = fullName
  state.joinError = null
  state.connectionId = newSocketId
  state.isTeacherUrl = isTeacher
  state.gameCode = gameCode
  state.mode = mode
  state.disableMemes = disableMemes
  state.countScreens = countScreens
  state.classId = classId || null
  state.className = className || null
  state.isReview = isReview
  state.canReviewPrevScreen = canReviewPrevScreen
  state.canReviewNextScreen = canReviewNextScreen
  state.isBusinessVersion = isBusinessVersion
  state.theme = theme || null

  playEnvironment.connectionId = newSocketId
  playEnvironment.fullName = fullName
  playEnvironment.oldGameCode = gameCode

  console.log("reconnect successful", {
    isReview,
    originalScreen,
    reportedAnswer,
  })

  let reviewData: ReviewData | undefined
  if (isReview) {
    if (originalScreen) {
      reviewData = {
        originalScreen,
        answer: reportedAnswer,
        connectItems,
      }
    }

    state.nextScreenButtonIsVisible = state.nextScreenButtonIsActive =
      canReviewNextScreen
    state.prevScreenButtonIsActive = state.prevScreenButtonIsVisible =
      canReviewPrevScreen
  }

  if (screen || gameStarted) {
    applyScreenToState(state, state.screen, reviewData)
  }

  console.log("reconnect, gameStarted", gameStarted, "classId", state.classId)
  if (!gameStarted) {
    if (mode === SessionMode.Classroom) {
      state.loadingReason = "ClassroomLobby"
    } else {
      state.loadingReason = "Loading"
    }
    console.log("Loading reason", state.loadingReason)
    state.internalPage = "Loading"
  } else if (state.imagesToPreload.length > 0) {
    state.nextTimer = payload
    state.loadingReason = "LoadingImages"
    state.internalPage = "Loading"
  } else {
    state.internalPage = null
    state.nextTimer = null
    applyTimerToState(state, payload)
  }

  if (gameStarted && !isReview && payload.screenAnswered) {
    // a classroom stays on the screen until the teacher moves on: wait as
    // after the answer, or the student answers it again
    state.finished = true
    state.loadingReason = "OtherStudentsAnswering"
    state.internalPage = "Loading"
  }

  if (mode !== SessionMode.Homework) {
    startListeningForMessages({
      firstTime: true,
      hasTimer: payload.timerEnabledForSession,
    })
  }
}

export function reconnectFailedReducer(state: PlayHubState) {
  console.log("reconnectFailedAction")
  state.connectionId = null
  playEnvironment.connectionId = null

  // stop listening for messages
  console.log("stop listening for messages")
  state.isStudentRemoved = true

  if (state.isTeacherUrl) {
    console.log("TODO: reconnect teacher")
    state.internalPage = "Lobby"
    // ??
  } else {
    if (state.screen) {
      state.internalPage = null
    } else {
      state.internalPage = "GameCode"
    }
  }
}

export function serverRequestErrorReducer(
  state: PlayHubState,
  payload?: unknown
) {
  console.log("Request failed", payload)
  state.serverErrorData = payload
  state.serverError = "Generic"
  genericServerRequestErrorReducer(state)
}

export function applyElapsedSecondsToState(
  state: PlayHubState,
  payload: {
    timerSecondsRemaining: number
    timerTotalSeconds?: number
  }
) {
  let timerTotalSeconds: number
  if (typeof payload.timerTotalSeconds === "number") {
    timerTotalSeconds = payload.timerTotalSeconds
  } else {
    timerTotalSeconds = state.timer.totalSeconds
  }

  state.timer.timerOffsetMs =
    Math.max(0, timerTotalSeconds - payload.timerSecondsRemaining) * 1_000
  applyElapsedMsToState(state, { elapsedMs: state.timer.timerOffsetMs })
}

export function applyElapsedMsToState(
  state: PlayHubState,
  payload: { elapsedMs: number }
) {
  const { elapsedMs } = payload
  const totalMs = state.timer.totalSeconds * 1_000
  if (state.timer.totalSeconds > 0) {
    state.timer.progress = elapsedMs / totalMs
    state.timer.secondsRemaining = Math.max(
      0,
      state.timer.totalSeconds - Math.floor(elapsedMs / 1_000)
    )
  } else {
    // console.log('timerTick: totalSeconds <= 0')
    state.timer.progress = 0
    state.timer.secondsRemaining = 0
  }
}

export function disableTimer(state: PlayHubState) {
  state.timer = emptyTimerState(state.timer)
}

export function applyTimerToState(state: PlayHubState, args: NextTimerState) {
  if (state.isReview || state.isPreview || !args.timerVisible) {
    // console.log('applyTimerToState, disabling')
    disableTimer(state)
  } else {
    const {
      timerEnabledForSession,
      timerRunning,
      timerVisible,
      timerTotalSeconds,
    } = args
    let { timerSecondsRemaining } = args

    state.timer.enabledForSession = timerEnabledForSession

    let newRunning = timerRunning
    if (!timerVisible) {
      // cannot run when invisible
      newRunning = false
    }

    if (newRunning && newRunning !== state.timer.running) {
      // clean on transition
      state.timer.initialTimestamp = null
    }

    state.timer.visible = timerVisible
    state.timer.running = newRunning
    state.timer.totalSeconds = timerTotalSeconds

    if (typeof timerSecondsRemaining !== "number") {
      timerSecondsRemaining = timerTotalSeconds
    }

    state.timer.secondsRemaining = timerSecondsRemaining

    applyElapsedSecondsToState(state, {
      timerTotalSeconds,
      timerSecondsRemaining,
    })
  }

  // console.log('applyTimerToState: running', state.timer.running, 'seconds', state.timer.totalSeconds, 'remaining', state.timer.secondsRemaining)
}

// note: in separate file in order to resolve
// import conflicts
function studentAnsweredRequest(
  state: PlayHubState,
  request: () => Promise<ScreenFinishedResponse | undefined>,
  action: PlayErrorAction = "Answer"
): Promise<void> {
  state.navigationIsLoading = true
  clearPlayErrorReducer(state)
  disableTimer(state)

  const screenKey = `${state.connectionId}|${state.screen?.id}`

  return oncePerUpdate(() => {
    if (screenKey === s_answeredScreen) {
      // the timer ran out while the answer was on its way (TimerTimedOut in a
      // classroom, the frame of the click in a homework): the answer's own
      // response moves the page on
      return Promise.resolve()
    }

    s_answeredScreen = screenKey
    return sendAnswer(request, action, screenKey)
  })
}

// The screen this page has sent an answer for (`connectionId|screenId`), until
// the request fails. Kept outside the state: an update React runs again on top
// of one that waited (the timer's, when it ran out in the frame of a click)
// sees the state from before the click, not the answer it sent.
let s_answeredScreen: string | null = null

function sendAnswer(
  request: () => Promise<ScreenFinishedResponse | undefined>,
  action: PlayErrorAction,
  screenKey: string
): Promise<void> {
  return request()
    .then((resp) => {
      globalModifyPlayState((state) => {
        // note: this is set in screenFinishedAction, therefore it should be in
        // loading state until screen change actually happens
        // state.navigationIsLoading = false

        state.navigationIsLoading = false

        if (state.mode === SessionMode.Classroom) {
          if (state.isTeacherUrl) {
            // do nothing, wait for broadcast event
            // since we initiated the screen change, it won't take long, and the UI is disabled anyway
          } else {
            // wait for broadcast event, but screen change will happen
            // only when all have answered (broadcast) or the teacher changes the screen =>
            // we have to wait longer
            state.loadingReason = "OtherStudentsAnswering"
            state.internalPage = "Loading"
            state.nextTimer = resp || null
          }
        } else {
          // load immediately
          screenFinishedReducer(state, resp)
        }
      })
    })
    .catch((e) => {
      // failed: trying again must send it
      if (s_answeredScreen === screenKey) s_answeredScreen = null

      globalModifyPlayState((state) => {
        state.navigationIsLoading = false
        serverRequestErrorReducer(state, e)
        playErrorReducer(state, action, e)
      })
    })
}

export function requestNextScreenReducer(state: PlayHubState): Promise<void> {
  console.log("requestNextScrenReducer")
  state.finished = true

  disableTimer(state)

  if (!state.connectionId) {
    return Promise.reject("no connection id!")
  }
  if (!state.nextScreenButtonIsVisible) {
    return Promise.reject("prev button must be visible")
  }
  if (state.navigationIsLoading) {
    return Promise.reject("prev button must not be loading")
  }
  state.navigationIsLoading = true

  if (!state.nextScreenButtonIsActive) {
    return Promise.reject("prev button must be active")
  }

  if (state.isReview) {
    if (!state.canReviewNextScreen) {
      return Promise.reject("can't review next screen")
    }

    clearPlayErrorReducer(state)
    const connectionId = state.connectionId
    return oncePerUpdate(() =>
      serverPlayReviewNext(connectionId)
        .then((resp) => {
          console.log("serverPlayReviewNext successful")
          globalModifyPlayState((state) => {
            reviewRequestSucceededReducer(state, resp)
          })
        })
        .catch((e) => {
          console.log("serverPlayReviewNext failure")
          globalModifyPlayState((state) => {
            reviewRequestFailedReducer(state, e, "ReviewNext")
          })
        })
    )
  } else {
    if (state.isTeacherUrl) {
      return studentAnsweredRequest(
        state,
        () =>
          serverPlayNextScreen(state.connectionId!, {
            isTimeout: false,
            screenId: state.screen?.id!,
          }),
        "NextScreen"
      )
    } else {
      return finishScreenReducer(state, { timeout: false })
    } // not review, not teacher
  } // not review
}

export function applyScreenToState(
  state: PlayHubState,
  screen: ClientScreen | null | undefined,
  reviewData?: ReviewData
) {
  state.screen = screen
  clearPlayErrorReducer(state)
  state.dragDrop = emptyDragDrop()
  state.quizAnswers = []
  state.map = emptyMap()
  state.keyboard = emptyKeyboard()
  state.connect = emptyConnect()
  state.drawing = emptyDrawing()
  state.pollAnswers = []
  state.openText = emptyOpenText()
  state.loadingReason = null
  state.nextScreenButtonIsVisible = true
  state.nextScreenButtonIsActive = false
  state.showCorrectAnswers = false
  state.showAnswerExplanation = false

  state.finished = false

  if (!!screen) {
    state.screenIndex = screen.currentScreen

    if (state.isTeacherUrl) {
      state.nextScreenButtonIsActive = true
    }

    if (reviewData) {
      state.finished = true
      state.showCorrectAnswers = true

      if (hasText(screen.answerExplanation)) {
        state.showAnswerExplanation = true
      }
    }

    switch (screen.screenType) {
      case ScreenType.Connect:
        applyConnectToState(state, screen, reviewData)
        break
      case ScreenType.DragDrop:
        applyDragDropToState(state, screen, reviewData)
        break
      case ScreenType.Quiz:
        applyQuizToState(state, screen, reviewData)
        break
      case ScreenType.Presentation:
        applyPresentationToState(state)
        break
      case ScreenType.Keyboard:
        applyKeyboardToState(state, screen, reviewData)
        break
      case ScreenType.Map:
        applyMapToState(state, screen, reviewData)
        break
      case ScreenType.Drawing:
        applyDrawToState(state, screen, reviewData)
        break
      case ScreenType.Poll:
        applyPollToState(state, screen, reviewData)
        break
      case ScreenType.OpenText:
        applyOpenTextToState(state, screen, reviewData)
        break
    }

    if (state.isReview) {
      if (state.canReviewNextScreen) {
        state.nextScreenButtonIsVisible = true
        state.nextScreenButtonIsActive = true
      } else {
        state.nextScreenButtonIsVisible = false
        state.nextScreenButtonIsActive = false
      }
    }

    if (state.canReviewPrevScreen) {
      state.prevScreenButtonIsActive = true
      state.prevScreenButtonIsVisible = true
    } else {
      state.prevScreenButtonIsActive = false
      state.prevScreenButtonIsVisible = false
    }
  } else {
    // final screen
    state.screen = null
    state.internalPage = "FinalScreen"
  }
}

function maybeShowLeaderboardOrMeme(
  state: PlayHubState,
  payload: ScreenFinishedResponse
) {
  // console.log('maybeShowLeaderboardOrMeme', payload.showFeedbackForMs)

  let durationInSeconds = payload.showFeedbackForMs / 1_000
  state.feedbackDurationInSeconds = durationInSeconds

  if (payload.feedback === "Meme") {
    // console.log('showing meme', payload.memeUrl, durationInSeconds)

    state.screenFinishedResponse = payload
    state.leaderboard = []
    state.lastScreenPoints = payload.lastScreenPoints
    state.lastScreenIsCorrect = payload.lastScreenCorrect
    state.memeUrl = payload.memeUrl
    state.internalPage = "ShowMeme"

    return "Shown"
  } else if (payload.feedback === "Leaderboard") {
    // console.log('showing leaderboard', durationInSeconds)

    state.screenFinishedResponse = payload
    state.leaderboard = payload.leaderboard

    state.lastScreenPoints = payload.lastScreenPoints
    state.lastScreenIsCorrect = payload.lastScreenCorrect
    state.internalPage = "StudentLeaderboard"

    return "Shown"
  }

  return "Not Shown"
}

export function screenFinishedReducer(
  state: PlayHubState,
  payload: ScreenFinishedResponse | undefined | null
) {
  console.log("screenFinishedAction", payload)

  state.serverError = null
  state.serverErrorData = undefined
  state.navigationIsLoading = false

  if (!payload) {
    // this indicates that the game has finished!
    if (
      state.isTeacherUrl &&
      state.screen !== null &&
      state.countScreens === state.screenIndex - 1
    ) {
      state.screen = null
      state.gameCompleted = true
      state.internalPage = "FinalScreen"
    }
    return
  }

  // in case of race condition - meme/leaderboard tries to go back to invalid screen,
  // override meme/leaderboard settings with the latest data
  state.screenFinishedResponse = payload

  state.correctAnswerText = payload.correctAnswer
  state.lastScreenIsCorrect = payload.lastScreenCorrect
  state.lastScreenPoints = payload.lastScreenPoints
  state.isReview = payload.isReview
  state.canReviewNextScreen = payload.canReviewNextScreen
  state.canReviewPrevScreen = payload.canReviewPrevScreen
  state.newGameLink = payload.newGameLink
  state.newGameMessage = payload.newGameMessage
  state.points = payload.totalPoints

  if (typeof payload.reportId === "string") {
    state.reportId = payload.reportId
  } else {
    state.reportId = null
  }

  // NOTE: disable timer for now - because we might have to
  // show correct answer, meme, leaderboard, and THEN enable timer
  // for now, just hide it
  state.timer.running = false

  // determine how to handle the existing screen before switching to the new screen
  // review screens just show readonly content and/or correct answers
  if (state.screen) {
    if (state.screen.screenType === ScreenType.Connect) {
      applyFinalConnectPairs(state, payload.connectItems)
    }

    let durationMS = 0
    let showCorrectAnswerAndExplanation = false
    if (payload.showCorrectForMs > 0) {
      showCorrectAnswerAndExplanation = true
      durationMS = payload.showCorrectForMs
    }
    if (payload.isReview) {
      showCorrectAnswerAndExplanation = true
      durationMS = 0
    }

    if (showCorrectAnswerAndExplanation) {
      state.showCorrectAnswers = true
      state.showAnswerExplanation =
        hasText(state.screen?.answerExplanation) &&
        (state.isTeacherUrl || !payload.lastScreenCorrect)
      state.internalPage = null

      if (!state.isReview) {
        state.nextScreenButtonIsVisible = false
      } else {
        console.log("???")
      }

      if (durationMS > 0) {
        setTimeout(function () {
          globalModifyPlayState((state) => {
            // showCorrectFinishedRecuder
            if (maybeShowLeaderboardOrMeme(state, payload) === "Not Shown") {
              state.nextScreenButtonIsVisible =
                state.mode == SessionMode.Homework || state.isTeacherUrl

              applyTimerToState(state, payload)
              switchToNextScreenReducer(state, payload)
            }
          })
        }, durationMS)
      }
      return
    } else {
      if (maybeShowLeaderboardOrMeme(state, payload) === "Shown") {
        return
      }
    }

    if (
      payload.feedback == "None" &&
      state.isTeacherUrl &&
      payload.showFeedbackForMs > 0
    ) {
      state.loadingReason = "TeacherWaitsForStudentFeedback"
      state.internalPage = "Loading"
      state.nextTimer = payload
      state.feedbackDurationInSeconds = payload.showFeedbackForMs / 1000
      return
    }
  } else {
    // there wasn't a screen before, this is probably the first
    // screen (or after reconnect), so just switch to the one in the payload
    // .. or this is a review, in which case the previous screen is just ignored
  }

  // if we go there, no meme or leaderboard is displayed, therefore apply timer
  applyTimerToState(state, payload)

  switchToNextScreenReducer(state, payload)
}

export function switchToNextScreenReducer(
  state: PlayHubState,
  payload: ScreenFinishedResponse
) {
  // console.log('switchToNextScreen')

  const {
    screen,
    correctAnswer,
    totalPoints,
    lastScreenPoints,
    lastScreenCorrect,
    leaderboard,
    accuracy,
    memeUrl,
    mode,
    lastCorrectAnswerForQuiz,
    reportId,
  } = payload

  state.screen = screen
  state.correctAnswerText = correctAnswer
  state.points = totalPoints
  state.lastScreenPoints = lastScreenPoints
  state.lastScreenIsCorrect = lastScreenCorrect
  state.leaderboard = leaderboard
  state.accuracy = accuracy
  state.memeUrl = memeUrl
  state.mode = mode
  state.correctAnswerIds = lastCorrectAnswerForQuiz
  state.internalPage = null
  state.countAnsweredStudents = 0
  state.isReview = payload.isReview
  state.reportId = reportId || null

  state.canReviewPrevScreen = payload.canReviewPrevScreen
  state.canReviewNextScreen = payload.canReviewNextScreen

  applyScreenToState(state, screen)
}

export function finishScreenReducer(
  state: PlayHubState,
  finishOptions: FinishScreenOptions
): Promise<void> {
  state.finished = true

  if (!state.connectionId) {
    return Promise.reject("no connection id!")
  }

  if (!state.screen) {
    return Promise.reject("screen is null!")
  }

  switch (state.screen.screenType) {
    case ScreenType.Quiz:
      return finishQuiz(state, finishOptions)

    case ScreenType.Map:
      return finishMapScreen(state, finishOptions)

    case ScreenType.Keyboard:
      return finishKeyboardScreen(state, finishOptions)

    case ScreenType.Drawing:
      return finishDrawingScreen(state, finishOptions)

    case ScreenType.DragDrop:
      return finishDragDropScreen(state, finishOptions)

    case ScreenType.Connect:
      return finishConnectScreen(state, finishOptions)

    case ScreenType.Poll:
      return finishPoll(state, finishOptions)

    case ScreenType.OpenText:
      return finishOpenTextScreen(state, finishOptions)

    default:
      return studentAnsweredRequest(
        state,
        () =>
          serverPlayNextScreen(state.connectionId!, {
            isTimeout: finishOptions.timeout,
            screenId: state.screen?.id!,
          }),
        "NextScreen"
      )
  }
}

export function reviewRequestSucceededReducer(
  state: PlayHubState,
  resp: ReviewScreenResponse
) {
  console.log("reviewRequestSucceeded")

  state.navigationIsLoading = false

  state.canReviewPrevScreen = resp.canReviewPrevScreen
  state.canReviewNextScreen = resp.canReviewNextScreen
  state.prevScreenButtonIsVisible = state.canReviewPrevScreen
  state.screenIndex = resp.screenIndex
  state.isReview = !!resp.originalScreen

  state.timer.visible = resp.timerVisible
  if (typeof resp.timerSecondsRemaining === "number") {
    applyElapsedSecondsToState(state, {
      timerSecondsRemaining: resp.timerSecondsRemaining,
      timerTotalSeconds: resp.timerTotalSeconds,
    })
  }

  if (
    !resp.timerVisible ||
    (typeof resp.timerSecondsRemaining === "number" &&
      resp.timerSecondsRemaining <= 0)
  ) {
    console.log(
      "disable timer in review callback visible: %s, timerSecondsRemaining: %o",
      yesno(resp.timerVisible),
      resp.timerSecondsRemaining
    )
    disableTimer(state)
  }

  state.internalPage = null
  // if there is race condition and meme/leaderboard tries to
  // navigate - stop it
  state.screenFinishedResponse = null

  let reviewData: ReviewData | undefined = undefined

  if (resp.originalScreen) {
    state.isReview = true
    state.timer.running = false

    reviewData = {
      originalScreen: resp.originalScreen,
      answer: resp.reportedAnswer,
      connectItems: resp.connectItems,
    }
  } else {
    state.isReview = false
    applyTimerToState(state, resp)
  }

  applyScreenToState(state, resp.screen, reviewData)
}

export function reviewRequestFailedReducer(
  state: PlayHubState,
  e: unknown,
  action: PlayErrorAction
) {
  state.navigationIsLoading = false
  state.serverErrorData = e
  genericServerRequestErrorReducer(state)
  playErrorReducer(state, action, e)
}

function convertConnectPairToLeftAndRight(args: {
  leftItems: ClientConnectItem[]
  rightItems: ClientConnectItem[]
}): { left: PlayConnectItem[]; right: PlayConnectItem[] } {
  const left: PlayConnectItem[] = args.leftItems.map((x) => {
    return {
      id: x.id,
      text: x.text,
      url: x.url,
      width: x.width,
      height: x.height,
      type: connectItemType(x.width || 0, x.height || 0),
      isSelected: false,
      ordinal: Math.random(),
      connectedToIdx: -1,
      colorIndex: -1,
      isCorrect: null,
    }
  })

  const right: PlayConnectItem[] = args.rightItems.map((x) => ({
    id: x.id,
    text: x.text,
    url: x.url,
    width: x.width,
    height: x.height,
    type: connectItemType(x.width || 0, x.height || 0),
    isSelected: false,
    ordinal: Math.random(),
    connectedToIdx: -1,
    colorIndex: -1,
    isCorrect: null,
  }))

  return { left, right }
}

export function convertConnectPairToPlayPair(args: {
  pairs: ConnectPair[]
  shuffle: boolean
}): PlayConnectPair[] {
  const leftItems: ClientConnectItem[] = []
  const rightItems: ClientConnectItem[] = []

  for (let p of args.pairs) {
    leftItems.push({
      id: p.id,
      text: p.leftText,
      height: p.leftImageHeight,
      width: p.leftImageWidth,
      url: p.leftImageUrl,
    })
    rightItems.push({
      id: p.id,
      text: p.rightText,
      height: p.rightImageHeight,
      width: p.rightImageWidth,
      url: p.rightImageUrl,
    })
  }
  return convertClientToPlayPair({
    leftItems,
    rightItems,
    shuffle: args.shuffle,
  })
}

export function convertClientToPlayPair(args: {
  leftItems: ClientConnectItem[]
  rightItems: ClientConnectItem[]
  shuffle: boolean
}): PlayConnectPair[] {
  const { left, right } = convertConnectPairToLeftAndRight(args)

  const countPairs = right.length

  const result: PlayConnectPair[] = []
  for (let i = 0; i < countPairs; i++) {
    result.push({
      left: left[i],
      right: right[i],
    })
  }

  return result
}

export function applyFinalConnectPairs(
  state: PlayHubState,
  connectItems: ConnectFinishedItem[]
) {
  const leftMap = new Map<string, number>()
  const rightMap = new Map<string, number>()

  state.connect.pairs.forEach((pair, idx) => {
    leftMap.set(pair.left.id, idx)
    rightMap.set(pair.right.id, idx)
  })

  // state.connect.pairs.forEach((connectPair, connectPairIndex) => {
  //   const leftAnswer = connectItems.find(x => x.leftId === connectPair.left.id);
  //
  //   if (leftAnswer) {
  //     connectPair.left.isCorrect = leftAnswer.isCorrect;
  //   }
  //
  //   const rightAnswer = connectItems.find(x => x.rightId === connectPair.right.id);
  //
  //   if (rightAnswer) {
  //     connectPair.right.isCorrect = rightAnswer.isCorrect;
  //   }
  // })

  connectItems.forEach((link, linkIndex) => {
    const leftPairIdx = leftMap.get(link.leftId)
    const rightPairIdx = rightMap.get(link.rightId)

    if (
      typeof leftPairIdx === "undefined" ||
      typeof rightPairIdx === "undefined"
    ) {
      return
    }

    const leftPair = state.connect.pairs[leftPairIdx]
    leftPair.left.isCorrect = link.isCorrect

    if (leftPair.left.connectedToIdx === -1) {
      leftPair.left.connectedToIdx = rightPairIdx
    }
    if (leftPair.left.colorIndex === -1) {
      leftPair.left.colorIndex = linkIndex
    }

    const rightPair = state.connect.pairs[rightPairIdx]
    rightPair.right.isCorrect = link.isCorrect

    if (rightPair.right.connectedToIdx === -1) {
      rightPair.right.connectedToIdx = leftPairIdx
    }
    if (rightPair.right.colorIndex === -1) {
      rightPair.right.colorIndex = linkIndex
    }
  })
}

function applyConnectToState(
  state: PlayHubState,
  screen: ClientScreen,
  reviewData?: ReviewData
) {
  state.answerExplanationPosition = "bottom"

  const finalConnectItems = reviewData?.connectItems

  if (!Array.isArray(finalConnectItems)) {
    state.connect.pairs = convertClientToPlayPair({
      leftItems: screen.connectLefts,
      rightItems: screen.connectRights,
      shuffle: false,
    })
  } else {
    state.connect.pairs = convertClientToPlayPair({
      leftItems: screen.connectLefts,
      rightItems: screen.connectRights,
      shuffle: false,
    })
    applyFinalConnectPairs(state, finalConnectItems)
  }
}

function finishConnectScreen(
  state: PlayHubState,
  finishOptions: FinishScreenOptions
) {
  if (!state.connectionId) {
    return Promise.reject("connectionId is empty")
  }

  // Build up connections
  // Note: walking one side only should bring up all connections together. Picking left on random
  const finalState: ConnectAnswerRequest = {
    pairs: [],
    isTimeout: finishOptions.timeout,
    screenId: state.screen?.id!,
  }
  const pairs = state.connect.pairs
  for (let pairIdx = 0, len = pairs.length; pairIdx < len; pairIdx++) {
    const pair = pairs[pairIdx]

    const left = pair.left
    const right = atOrNull(pairs, left.connectedToIdx)?.right
    if (!right) continue

    const finalPair: ConnectFinalStatePair = {
      leftId: left.id,
      rightId: right.id,
    }
    finalState.pairs.push(finalPair)
  }

  return studentAnsweredRequest(state, () =>
    serverPlayConnectAnswer(state.connectionId!, finalState)
  )
}

export function computeInitialDragDropStateFromScreen(args: {
  dragDropCategories: DragDropCategory[]
  shuffleDragDropZones: boolean
  shuffleDragDropAnswerPiles: boolean
}): PlayDragDropState {
  const {
    dragDropCategories,
    shuffleDragDropZones,
    shuffleDragDropAnswerPiles,
  } = args
  const dragDrop = emptyDragDrop()

  const allAnswers: PlayDragDropAnswer[] = []
  for (let dragDropCategory of dragDropCategories) {
    dragDrop.dropZones.push({
      categoryId: dragDropCategory.id,
      title: dragDropCategory.title,
      ordinal: Math.random(),
      isDraggedOver: false,
      targets: [
        { isDraggedFrom: false, answers: [] },
        { isDraggedFrom: false, answers: [] },
        { isDraggedFrom: false, answers: [] },
        { isDraggedFrom: false, answers: [] },
      ],
    })

    for (let answer of dragDropCategory.answers) {
      if (!hasText(answer.imageUrl) && !hasText(answer.text)) {
        continue
      }

      allAnswers.push({
        id: answer.id,
        categoryId: answer.categoryId,
        dropTargetIndex: -1,
        text: answer.text || "",
        imageUrl: answer.imageUrl,
        ordinal: Math.random(),
        imageType: answer.imageWidth >= answer.imageHeight ? "w" : "h",
      })
    }
  }

  if (shuffleDragDropZones) {
    dragDrop.dropZones.sort((x, y) => x.ordinal - y.ordinal)
  }

  if (shuffleDragDropAnswerPiles) {
    allAnswers.sort((x, y) => x.ordinal - y.ordinal)
  }

  if (allAnswers.length > 1) {
    dragDrop.answerPiles.push({
      isDraggedFrom: false,
      answers: [],
    })
    dragDrop.answerPiles.push({
      isDraggedFrom: false,
      answers: [],
    })

    const middle = Math.ceil(allAnswers.length / 2)
    allAnswers.forEach((answer, answerIndex) => {
      if (answerIndex < middle) {
        dragDrop.answerPiles[0].answers.push(answer)
      } else {
        dragDrop.answerPiles[1].answers.push(answer)
      }
    })
  } else if (allAnswers.length == 1) {
    dragDrop.answerPiles.push({
      isDraggedFrom: false,
      answers: [allAnswers[0]],
    })
  }

  return dragDrop
}

function applyDragDropToState(
  state: PlayHubState,
  screen: ClientScreen,
  reviewData?: ReviewData
) {
  state.answerExplanationPosition = "bottom"

  if (
    !reviewData ||
    !reviewData.answer ||
    reviewData.answer.type != ScreenType.DragDrop
  ) {
    state.dragDrop = computeInitialDragDropStateFromScreen({
      dragDropCategories: screen.dragDropCategories,
      shuffleDragDropZones: false,
      shuffleDragDropAnswerPiles: !state.isPreview,
    })

    let countAnswers = 0
    state.dragDrop.answerPiles.forEach((pile) =>
      pile.answers.forEach(() => {
        countAnswers++
      })
    )

    if (countAnswers > 0) {
      // no answers
      state.nextScreenButtonIsActive = true
    }
  } else {
    state.dragDrop = computeInitialDragDropStateFromScreen({
      dragDropCategories: screen.dragDropCategories,
      shuffleDragDropZones: false,
      shuffleDragDropAnswerPiles: false,
    })

    state.dragDrop.answerPiles.forEach((pile) => {
      pile.answers = []
    })

    const runningIndices = new Map<string, number>()

    reviewData.answer.moves.forEach((reportedMove) => {
      const destCategoryId =
        screen.dragDropCategories[reportedMove.toCategoryIndex].id

      const destCategory = state.dragDrop.dropZones.find(
        (x) => x.categoryId === destCategoryId
      )
      let answer: DragDropAnswer | undefined = undefined

      // Note: maybe stupid, but still supporting IE 11
      const countCategories = screen.dragDropCategories.length
      for (let idx = 0; idx < countCategories; idx++) {
        const cat = screen.dragDropCategories[idx]
        answer = cat.answers.find((x) => x.id == reportedMove.originalAnswerId)
        if (answer) break
      }

      if (!destCategory || !answer) return

      let runningIndex = runningIndices.get(destCategoryId)
      if (typeof runningIndex === "undefined") {
        runningIndex = 0
      }

      const targetIdx = runningIndex % destCategory.targets.length
      const target = destCategory.targets[targetIdx]

      target.answers.push({
        id: reportedMove.originalAnswerId,
        categoryId: destCategoryId,
        ordinal: runningIndex,
        dropTargetIndex: targetIdx,
        text: answer.text || "",
        imageType: answer.imageWidth >= answer.imageHeight ? "w" : "h",
        imageUrl: answer.imageUrl,
        isCorrect: reportedMove.isCorrect,
      })

      runningIndex++
      runningIndices.set(destCategoryId, runningIndex)
    })
  }
}

function finishDragDropScreen(
  state: PlayHubState,
  finishOptions: FinishScreenOptions
) {
  if (!state.connectionId) {
    return Promise.reject("connectionId is empty")
  }

  const finalState: DragDropFinalState = {
    byCategory: [],
    unansweredIds: [],
  }

  for (let pile of state.dragDrop.answerPiles) {
    for (let answer of pile.answers) {
      finalState.unansweredIds.push(answer.id)
    }
  }

  for (let zone of state.dragDrop.dropZones) {
    const byCategory: DragDropFinalState["byCategory"][0] = {
      answersIds: [],
      categoryId: zone.categoryId,
    }
    for (let target of zone.targets) {
      for (let answer of target.answers) {
        byCategory.answersIds.push(answer.id)
      }
    }
    finalState.byCategory.push(byCategory)
  }

  return studentAnsweredRequest(state, () =>
    serverPlayDragDropAnswer(state.connectionId!, {
      ...finalState,
      isTimeout: finishOptions.timeout,
      screenId: state.screen?.id!,
    })
  )
}

function applyDrawToState(
  state: PlayHubState,
  screen: ClientScreen,
  reviewData?: ReviewData
) {
  state.answerExplanationPosition = undefined
  state.nextScreenButtonIsActive = true
  state.nextScreenButtonIsVisible = true

  const answer = reviewData?.answer

  if (!answer || answer.type !== ScreenType.Drawing) return

  state.drawing.drawingSeries = answer.drawingCommands.map((x) => ({
    color: x.color,
    points: x.points,
    radius: x.stroke,
  }))
}

function finishDrawingScreen(
  state: PlayHubState,
  finishOptions: FinishScreenOptions
) {
  if (!state.connectionId) {
    return Promise.reject("connectionId is empty")
  }

  return studentAnsweredRequest(state, () =>
    serverPlayDrawingSubmit(state.connectionId!, {
      drawingSeries: state.drawing.drawingSeries,
      isTimeout: finishOptions.timeout,
      screenId: state.screen?.id!,
    })
  )
}

function applyKeyboardToState(
  state: PlayHubState,
  screen: ClientScreen,
  reviewData?: ReviewData
) {
  state.answerExplanationPosition = "bottom"

  if (
    reviewData &&
    reviewData.answer &&
    reviewData.answer.type === ScreenType.Keyboard
  ) {
    state.lastScreenIsCorrect = reviewData.answer.isCorrect
    state.keyboard.answer = reviewData.answer.answerText
  }
}

function finishKeyboardScreen(
  state: PlayHubState,
  finishOptions: FinishScreenOptions
) {
  if (!state.connectionId) {
    return Promise.reject("connectionId is empty")
  }

  return studentAnsweredRequest(state, () =>
    serverPlayKeyboardAnswer(state.connectionId!, {
      answerText: state.keyboard.answer,
      isTimeout: finishOptions.timeout,
      screenId: state.screen?.id!,
    })
  )
}

function applyMapToState(
  state: PlayHubState,
  screen: ClientScreen,
  reviewData?: ReviewData
) {
  state.answerExplanationPosition = "bottom"

  if (
    !reviewData ||
    !reviewData.answer ||
    reviewData.answer.type !== ScreenType.Map
  ) {
    if (screen.mapAnswers.length === 0 || !hasText(screen.questionImage)) {
      state.nextScreenButtonIsActive = true
    }
  } else {
    state.map.normalizedPin = {
      x: reviewData.answer.x,
      y: reviewData.answer.y,
    }
  }
}

function finishMapScreen(
  state: PlayHubState,
  finishOptions: FinishScreenOptions
) {
  const pin = state.map.normalizedPin || invalidMapPin()

  state.finished = true
  if (!hasText(state.connectionId)) {
    return Promise.reject("Connection id is null")
  }
  if (!hasText(state.gameCode)) {
    return Promise.reject("Game code is null")
  }

  return studentAnsweredRequest(state, () =>
    serverPlayMapAnswer(state.connectionId!, {
      x: pin.x,
      y: pin.y,
      isTimeout: finishOptions.timeout,
      screenId: state.screen?.id!,
    })
  )
}

function applyOpenTextToState(
  state: PlayHubState,
  screen: ClientScreen,
  reviewData?: ReviewData
) {
  state.answerExplanationPosition = "bottom"

  if (
    reviewData &&
    reviewData.answer &&
    reviewData.answer.type === ScreenType.OpenText
  ) {
    state.lastScreenIsCorrect = reviewData.answer.isCorrect
    state.openText.answer = reviewData.answer.answerText
  }
}

function finishOpenTextScreen(
  state: PlayHubState,
  finishOptions: FinishScreenOptions
) {
  if (!state.connectionId) {
    return Promise.reject("connectionId is empty")
  }

  return studentAnsweredRequest(state, () =>
    serverPlayOpenTextAnswer(state.connectionId!, {
      answerText: state.openText.answer,
      isTimeout: finishOptions.timeout,
      screenId: state.screen?.id!,
    })
  )
}

function applyPollToState(
  state: PlayHubState,
  screen: ClientScreen,
  reviewData?: ReviewData
) {
  state.answerExplanationPosition = "bottom"
  state.pollAnswers = screen.pollAnswers
    .map((x) => ({
      ...x,
      text: sanitizeEditorPreviewHtml(x.text || ""),
    }))
    .filter(
      (x) => (typeof x.url === "string" && x.url.length > 0) || hasText(x.text)
    )
    .map((x) => ({
      ...x,
      isSelected: false,
      ordinal: Math.random(),
    }))

  const reviewScreen = reviewData?.originalScreen
  const reviewAnswer = reviewData?.answer

  if (
    !reviewAnswer ||
    !reviewScreen ||
    reviewAnswer.type !== ScreenType.Poll ||
    reviewScreen.type !== ScreenType.Poll
  ) {
    if (state.pollAnswers.length === 0) {
      state.nextScreenButtonIsActive = true
    }
  } else {
    for (let answerIndex of reviewAnswer.answerIndexes) {
      const originalAnswer = reviewScreen.answers.find(
        (x) => x.ordinal == answerIndex
      )
      if (!originalAnswer) continue

      const a = state.pollAnswers.find((x) => x.id === originalAnswer.id)
      if (!a) continue

      a.isSelected = true
    }
  }
}

function finishPoll(state: PlayHubState, finishOptions: FinishScreenOptions) {
  const connectionId = state.connectionId
  if (!connectionId) {
    return Promise.reject("connectionId is empty!")
  }

  const selectedAnswerIds = state.pollAnswers
    .filter((x) => x.isSelected && hasText(x.id))
    .map((x) => x.id!)

  return studentAnsweredRequest(state, () =>
    serverPlayPollAnswer(connectionId, {
      answerIds: selectedAnswerIds,
      isTimeout: finishOptions.timeout,
      screenId: state.screen?.id!,
    })
  )
}

function applyPresentationToState(state: PlayHubState) {
  state.answerExplanationPosition = undefined
  state.finished = true
  state.nextScreenButtonIsVisible =
    state.mode == SessionMode.Homework || state.isTeacherUrl
  state.nextScreenButtonIsActive =
    state.mode == SessionMode.Homework || state.isTeacherUrl
}

function applyQuizToState(
  state: PlayHubState,
  screen: ClientScreen,
  reviewData?: ReviewData
) {
  state.answerExplanationPosition = "bottom"
  state.quizAnswers = screen.quizAnswers
    .map((x) => ({
      ...x,
      text: sanitizeEditorPreviewHtml(x.text),
    }))
    .filter(
      (x) => (typeof x.url === "string" && x.url.length > 0) || hasText(x.text)
    )
    .map((x) => ({
      ...x,
      isSelected: false,
      ordinal: Math.random(),
    }))

  const reviewScreen = reviewData?.originalScreen
  const reviewAnswer = reviewData?.answer

  if (
    !reviewAnswer ||
    !reviewScreen ||
    reviewAnswer.type !== ScreenType.Quiz ||
    reviewScreen.type !== ScreenType.Quiz
  ) {
    if (state.quizAnswers.length === 0) {
      state.nextScreenButtonIsActive = true
    }
  } else {
    for (let answerIndex of reviewAnswer.answerIndexes) {
      const originalAnswer = reviewScreen.answers.find(
        (x) => x.ordinal == answerIndex
      )
      if (!originalAnswer) continue

      const a = state.quizAnswers.find((x) => x.id === originalAnswer.id)
      if (!a) continue

      a.isSelected = true
    }

    for (let answer of state.quizAnswers) {
      const originalAnswer = reviewScreen.answers.find(
        (x) => x.id === answer.id
      )
      if (originalAnswer) answer.isCorrect = originalAnswer.isCorrect
    }
  }
}

function finishQuiz(state: PlayHubState, finishOptions: FinishScreenOptions) {
  const connectionId = state.connectionId
  if (!connectionId) {
    return Promise.reject("connectionId is empty!")
  }

  const selectedAnswerIds = state.quizAnswers
    .filter((x) => x.isSelected && hasText(x.id))
    .map((x) => x.id!)

  return studentAnsweredRequest(state, () =>
    serverPlayQuizAnswer(connectionId, {
      answerIds: selectedAnswerIds,
      isTimeout: finishOptions.timeout,
      screenId: state.screen?.id!,
    })
  )
}
